import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { ThemeContext } from '../ThemeContext';
import { UserContext } from '../UserContext';
import { useAnnouncementBar } from './AnnouncementBarContext';
import api from '../api';

const AnnouncementBar = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const { user } = useContext(UserContext);
  const location = useLocation();
  const { setIsVisible: setContextIsVisible, setHeight: setContextHeight } = useAnnouncementBar();
  const [isVisible, setIsVisible] = useState(true);
  const [isAutoClosing, setIsAutoClosing] = useState(false);
  const autoCloseTimerRef = React.useRef(null);
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(null);
  const [showReferralLink, setShowReferralLink] = useState(false);
  const countdownIntervalRef = React.useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [barHeight, setBarHeight] = useState(0);
  const barRef = React.useRef(null);

  // Measure and update bar height, and sync with context
  useEffect(() => {
    if (barRef.current && isVisible) {
      const height = barRef.current.offsetHeight;
      setBarHeight(height);
      setContextHeight(height);
      setContextIsVisible(true);
    } else {
      setBarHeight(0);
      setContextHeight(0);
      setContextIsVisible(false);
    }
  }, [isVisible, message, countdown, setContextHeight, setContextIsVisible]);

  // Determine message based on user status
  useEffect(() => {
    const determineMessage = async () => {
      // Use UserContext user if available, otherwise check localStorage
      let currentUser = user;
      if (!currentUser) {
        const userStr = typeof window !== 'undefined' ? localStorage.getItem('sat_user') : null;
        currentUser = userStr ? JSON.parse(userStr) : null;
      }

      // Case 1: User not logged in
      if (!currentUser) {
        setMessage('Try it today for FREE for 7 days! Start your journey to a better SAT score now.');
        setShowReferralLink(false);
        setCountdown(null);
        // Clear any existing countdown interval
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        return;
      }

      // Case 2: Logged in, not premium, trial not used
      if (!currentUser.is_premium && !currentUser.trial_used) {
        setMessage('Try it today for FREE for 7 days! Start your journey to a better SAT score now.');
        setShowReferralLink(false);
        setCountdown(null);
        // Clear any existing countdown interval
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        return;
      }

      // Case 3: Premium, trial used, is_free = true (trial period)
      if (currentUser.is_premium && currentUser.trial_used && currentUser.is_free) {
        try {
          // Fetch subscription to get created_at date
          const subResponse = await api.get(`/api/stripe/get-subscription/`);
          const subscription = subResponse.data?.subscription;
          
          if (subscription && subscription.current_period_start) {
            // Calculate countdown from created_at to 7 days
            const createdDate = new Date(subscription.current_period_start * 1000); // Convert from timestamp
            const trialEndDate = new Date(createdDate.getTime() + 7 * 24 * 60 * 60 * 1000); // Add 7 days
            
            const updateCountdown = () => {
              const now = new Date();
              const diff = trialEndDate - now;
              
              if (diff <= 0) {
                setCountdown(null);
                setMessage('Your trial has expired.');
                if (countdownIntervalRef.current) {
                  clearInterval(countdownIntervalRef.current);
                  countdownIntervalRef.current = null;
                }
                return;
              }
              
              const days = Math.floor(diff / (1000 * 60 * 60 * 24));
              const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
              const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
              const seconds = Math.floor((diff % (1000 * 60)) / 1000);
              
              setCountdown({ days, hours, minutes, seconds });
              setMessage(`Your trial expires in ${days}d ${hours}h ${minutes}m ${seconds}s`);
            };
            
            updateCountdown();
            
            // Clear existing interval before setting new one
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
            }
            countdownIntervalRef.current = setInterval(updateCountdown, 1000);
          } else {
            setMessage('Your trial expires soon!');
            setShowReferralLink(false);
            setCountdown(null);
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }
          }
        } catch (error) {
          console.error('Error fetching subscription:', error);
          setMessage('Your trial expires soon!');
          setShowReferralLink(false);
          setCountdown(null);
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
        }
        return;
      }

      // Case 4: Premium, trial used, is_free = false (paid subscription)
      if (currentUser.is_premium && currentUser.trial_used && !currentUser.is_free) {
        setMessage('Refer a friend and get 5000 points');
        setShowReferralLink(true);
        setCountdown(null);
        // Clear any existing countdown interval
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        return;
      }

      // Default fallback
      setMessage('Welcome to Game Your SAT');
      setShowReferralLink(false);
      setCountdown(null);
    };

    determineMessage();

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [user, location.pathname, refreshKey]); // Re-run when user, location, or refreshKey changes

  // Listen for storage changes and window focus to detect login/logout
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'sat_user' || e.key === null) {
        // User logged in/out in another tab, force re-evaluation
        setRefreshKey(prev => prev + 1);
      }
    };

    const handleFocus = () => {
      // When window regains focus, re-check user state
      const userStr = localStorage.getItem('sat_user');
      const currentUser = userStr ? JSON.parse(userStr) : null;
      // Force re-evaluation if user state changed
      if ((user && !currentUser) || (!user && currentUser)) {
        setRefreshKey(prev => prev + 1);
      }
    };

    // Listen for custom events that might be dispatched on login/logout
    const handleUserChange = () => {
      setRefreshKey(prev => prev + 1);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('userStateChanged', handleUserChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('userStateChanged', handleUserChange);
    };
  }, [user]);


  const handleClose = () => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    setIsVisible(false);
    setIsAutoClosing(false);
  };

  const handleReopen = () => {
    setIsVisible(true);
    // The useEffect will handle auto-closing after 10 seconds
  };

  return (
    <>
      {/* Spacer div that pushes content down when bar is visible */}
      <AnimatePresence mode="wait">
        {isVisible && (
          <motion.div
            key="spacer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: barHeight || 60, opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="w-full"
            style={{ 
              minHeight: barHeight || 60,
              transition: 'height 0.3s ease-in-out'
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isVisible && (
          <motion.div
            ref={barRef}
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.3 }}
            className="fixed top-0 left-0 right-0 z-[100] bg-[#06bae4] dark:bg-[#06bae4] text-white shadow-lg"
          >
            <div className="relative px-4 sm:px-6 lg:px-8 py-2 md:py-3">
              <div className="max-w-7xl mx-auto flex items-center justify-center gap-4">
                <p className="text-sm sm:text-base md:text-lg font-zonaProRegular text-center flex-1">
                  {countdown ? (
                    <span className="font-bold font-zonaProUltra">
                      Your trial expires in {countdown.days}d {countdown.hours}h {countdown.minutes}m {countdown.seconds}s
                    </span>
                  ) : message.includes('Try it today') ? (
                    <>
                      <Link 
                        to={user ? "/subscription" : "/signup"}
                        className="underline hover:no-underline font-bold font-zonaProUltra opacity-90 hover:opacity-100 transition-opacity"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Try it today for FREE for 7 days!
                      </Link>{' '}
                      Start your journey to a better SAT score now.
                    </>
                  ) : showReferralLink ? (
                    <>
                      <span className="font-bold font-zonaProUltra">{message}</span>{' '}
                      <Link 
                        to="/refer" 
                        className="underline hover:no-underline font-bold font-zonaProUltra opacity-90 hover:opacity-100 transition-opacity"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Click here
                      </Link>
                    </>
                  ) : (
                    <span className="font-bold font-zonaProUltra">{message}</span>
                  )}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="absolute right-2 sm:right-4 md:right-6 lg:right-8 top-1/2 -translate-y-1/2 flex-shrink-0 p-1 md:p-2 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="Close announcement"
              >
                <X size={20} className="text-white md:w-6 md:h-6" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reopen Button - Only shows when announcement is closed */}
      {!isVisible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={handleReopen}
          className="fixed top-2 right-2 sm:top-2 sm:right-3 md:top-2 md:right-4 z-[100] p-1 bg-[#06bae4] dark:bg-[#06bae4] text-white rounded-full shadow-lg hover:bg-[#05a3d1] dark:hover:bg-[#05a3d1] transition-all flex items-center justify-center"
          aria-label="Show announcement"
          title="Show announcement"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <ChevronDown size={20} className="md:w-6 md:h-6" />
        </motion.button>
      )}
    </>
  );
};

export default AnnouncementBar;

